import React, { useState } from 'react';
import {
  Box,
  Button,
  MobileStepper,
  Typography,
  Paper,
} from '@mui/material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useTheme } from '@mui/material/styles';
import { useCompany } from '../../contexts/CompanyContext';

import StepBasicInfo from './steps/StepBasicInfo';
import StepVisionValues from './steps/StepVisionValues';

const steps = [
  'Informações básicas',
  'Visão e valores'
];

const initialData = {
  name: '',
  logoUrl: '',
  bannerUrl: '',
  mission: '',
  vision: '',
  values: '',
  goals: '',
  internalPolicies: '',
  rules: '',
};

const CompanyStepper = () => {
  const { company, saveCompanyData } = useCompany();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(company || initialData);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const maxSteps = steps.length;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateFirstStep = () => {
    let error = '';
    if (!formData.name.trim()) { 
      error += 'O nome da empresa é obrigatório\n';
    }
    if (!formData.tempoSemanal.trim()) { 
      error += 'As horas semanais de funcionamento da empresa, é obrigatório\n';
    }
    if(error) {
      setError(error);
      return false;
    }
    return true;
  }

  const validateStep = () => {
    if (activeStep === 0) {
      return validateFirstStep();
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    if (activeStep === maxSteps - 1) {
      saveData();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const saveData = async () => {
    setSaving(true);
    
    try {
      saveCompanyData(formData);
      
      setActiveStep((prev) => prev + 1);
    } catch {
      setError('Erro ao salvar dados da empresa.');
    }
    setSaving(false);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <StepBasicInfo formData={formData} handleChange={handleChange} />;
      case 1:
        return <StepVisionValues formData={formData} handleChange={handleChange} />;
      default:
        return 'Passo desconhecido';
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        maxWidth: 700,
        mx: 'auto',
        p: { xs: 2, sm: 4 },
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 400,
      }}
    >
      <Typography variant="h5" fontWeight={700} textAlign="center" mb={3}>
        {activeStep < maxSteps
          ? steps[activeStep]
          : 'Cadastro concluído com sucesso!'}
      </Typography>

      {activeStep === maxSteps ? (
        <Box textAlign="center" py={6} flexGrow={1}>
          <Typography variant="h6" gutterBottom>
            Seus dados foram salvos e você pode continuar usando o sistema.
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 4, borderRadius: 2 }}
            onClick={() => setActiveStep(0)}
          >
            Editar Empresa
          </Button>
        </Box>
      ) : (
        <>
          <Box flexGrow={1}>{getStepContent(activeStep)}</Box>
          {error && (
            <Typography color="error" sx={{ mt: 2, textAlign: 'center',whiteSpace: 'pre-line', }}>
              {error}
            </Typography>
          )}
          <MobileStepper
            variant="progress"
            steps={maxSteps}
            position="static"
            activeStep={activeStep}
            nextButton={
              <Button
                size="small"
                onClick={handleNext}
                disabled={saving}
                sx={{ borderRadius: 2 }}
                aria-label={
                  activeStep === maxSteps - 1 ? 'Finalizar cadastro' : 'Próximo'
                }
              >
                {activeStep === maxSteps - 1 ? 'Finalizar' : 'Próximo'}
                {theme.direction === 'rtl' ? (
                  <KeyboardArrowLeft />
                ) : (
                  <KeyboardArrowRight />
                )}
              </Button>
            }
            backButton={
              <Button
                size="small"
                onClick={handleBack}
                disabled={activeStep === 0 || saving}
                sx={{ borderRadius: 2 }}
                aria-label="Voltar"
              >
                {theme.direction === 'rtl' ? (
                  <KeyboardArrowRight />
                ) : (
                  <KeyboardArrowLeft />
                )}
                Voltar
              </Button>
            }
            sx={{
              mt: 3,
            }}
          />
        </>
      )}
    </Paper>
  );
};

export default CompanyStepper;
